import { Injectable, inject, signal, computed } from '@angular/core';
import { Auth, User, authState } from '@angular/fire/auth';
import { RightType, GetUserRightsData } from '@dataconnect/copilot';
import { injectGetUserRights } from '@dataconnect/copilot/angular';
import { get, includes, some } from 'lodash-es';


@Injectable({
  providedIn: 'root',
})
export class RightsService {
  private auth: Auth = inject(Auth);
  private user = signal<User | null>(null);

  private getRights = injectGetUserRights(
    () => ({
      enabled: !!this.user(), // Enable when user is authenticated
      queryKey: ['user-rights', get(this.user(), 'uid')]
    })
  );

  // Computed signal for easy access to rights data
  rights = computed<GetUserRightsData['organizations']>(() => get(this.getRights.data(), 'organizations', []));

  constructor() {
    authState(this.auth).subscribe(user => {
      this.user.set(user);
    });
  }

  hasAny(anyRights: RightType | RightType[], organization?: { id: string, ancestors?: string[] | null }): boolean {
    const userRights = this.rights();
    const rightsToCheck = Array.isArray(anyRights) ? anyRights : [anyRights];

    if (this.user() === null) {
      return false;
    }

    if (!organization) {
      // Check if user has any of the rights in any organization
      return some(userRights, orgRights => some(rightsToCheck, right => includes(orgRights.rights, right)));
    }

    // Check if user has rights in this specific organization or its ancestors
    return some(userRights, orgRights => {
      const hasRights = some(rightsToCheck, right => includes(orgRights.rights, right));
      const inOrganization = orgRights.organization.id === organization.id;
      const inAncestor = includes(organization.ancestors, orgRights.organization.id);
      return hasRights && (inOrganization || inAncestor);
    });
  }

  async reloadRights(): Promise<void> {
    const user = this.user();
    if (user === null) {
      return;
    }
    await user.getIdToken(true);
    this.getRights.refetch();
  }
}
